import PageHeader from "../components/shell/PageHeader";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyDisplayOffset } from "../utils/displayTimezone";
import { usePageNav } from "../components/shell/PageNav";
import FlashToast from "../components/shell/FlashToast";
import useIsDesktop from "../hooks/useIsDesktop";
import {
  SendHorizontal,
  Play,
  Clock,
  Pencil,
  XCircle,
  MoveRight,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Minus,
  Plus,
  ChevronDown,
  Search,
  Wallet,
  Activity,
  Target,
  Layers3,
  GitBranchPlus,
  Globe,
} from "lucide-react";
import {
  apiAgentCommand,
  apiAgentOpenTrades,
  apiAgentStatus,
  apiAgentRegisterExisting,
  apiAgentHistory,
} from "../api/api";

const LS_TOKEN = "agent_token";

const storage = {
  get(key) {
    try {
      if (typeof window === "undefined") return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(key, value);
    } catch {}
  },
};

const pick = (obj, keys, fallback = undefined) => {
  for (const key of keys) {
    if (obj && obj[key] != null) return obj[key];
  }
  return fallback;
};

const toNumber = (value, fallback = 0) => {
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const nfloat = (value) => {
  if (value === "" || value == null) return undefined;
  const parsed = parseFloat(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
};

/* Meme point de passage unique que `tsToDate` du journal : le fuseau
   d'affichage choisi par l'utilisateur s'applique ici aussi, sinon les heures
   des positions contrediraient celles du journal. */
function toDate(value) {
  if (value == null) return null;
  if (typeof value === "number") {
    return applyDisplayOffset(new Date(value < 1e12 ? value * 1000 : value));
  }
  const d = new Date(value);
  return Number.isNaN(+d) ? null : applyDisplayOffset(d);
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatNumber = (value, digits = 2) =>
  toNumber(value, 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const inputClass =
  "fm-exec-input h-12 w-full rounded-2xl border border-slate-200/80 bg-white/95 px-4 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-violet-400/70 focus:ring-4 focus:ring-violet-500/10 dark:border-white/10 dark:bg-[#0f1529] dark:text-white dark:placeholder:text-white/28";

const selectClass =
  "fm-exec-input h-12 w-full rounded-2xl border border-slate-200/80 bg-white/95 px-4 text-sm text-slate-900 outline-none transition-all duration-200 focus:border-violet-400/70 focus:ring-4 focus:ring-violet-500/10 dark:border-white/10 dark:bg-[#0f1529] dark:text-white";

const panelClass =
  "fm-exec-panel rounded-2xl border border-slate-200 bg-white dark:border-[#26314a] dark:bg-[#0f1729]";

const cardClass =
  "fm-exec-card rounded-2xl border border-slate-200 bg-white dark:border-[#26314a] dark:bg-[#0f1729]";

const tabRailWrapClass =
  "fm-exec-tab-rail relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 p-1.5 dark:border-[#26314a] dark:bg-[#0b1220]";

const tabRailClass =
  "flex min-w-max items-stretch gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

const headerShellClass =
  "fm-exec-header rounded-2xl border border-slate-200 bg-white dark:border-[#26314a] dark:bg-[#0f1729]";

const compactStatCardClass =
  "fm-exec-stat min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 dark:border-[#26314a] dark:bg-[#0f1729]";

const DERIV_SYMBOL_LIBRARY = [
  "VOLATILITY 10 INDEX",
  "VOLATILITY 25 INDEX",
  "VOLATILITY 50 INDEX",
  "VOLATILITY 75 INDEX",
  "VOLATILITY 100 INDEX",
  "VOLATILITY 10 (1S) INDEX",
  "VOLATILITY 25 (1S) INDEX",
  "VOLATILITY 50 (1S) INDEX",
  "VOLATILITY 75 (1S) INDEX",
  "VOLATILITY 90 (1S) INDEX",
  "VOLATILITY 100 (1S) INDEX",
  "CRASH 300 INDEX",
  "CRASH 500 INDEX",
  "CRASH 600 INDEX",
  "CRASH 900 INDEX",
  "CRASH 1000 INDEX",
  "BOOM 300 INDEX",
  "BOOM 500 INDEX",
  "BOOM 600 INDEX",
  "BOOM 900 INDEX",
  "BOOM 1000 INDEX",
  "JUMP 10 INDEX",
  "JUMP 25 INDEX",
  "JUMP 50 INDEX",
  "JUMP 75 INDEX",
  "JUMP 100 INDEX",
  "STEP INDEX",
  "STEP 200 INDEX",
  "STEP 300 INDEX",
  "STEP 400 INDEX",
  "STEP 500 INDEX",
  "MULTI STEP 2 INDEX",
  "MULTI STEP 3 INDEX",
  "MULTI STEP 4 INDEX",
  "SKEW STEP 4 UP INDEX",
  "SKEW STEP 4 DOWN INDEX",
  "SKEW STEP 5 UP INDEX",
  "SKEW STEP 5 DOWN INDEX",
  "DRIFT SWITCHING INDEX 10",
  "DRIFT SWITCHING INDEX 20",
  "DRIFT SWITCHING INDEX 30",
  "DEX 600 UP",
  "DEX 900 UP",
  "DEX 1500 UP",
  "DEX 600 DN",
  "DEX 900 DN",
  "DEX 1500 DN",
  "RANGE BREAK 100 INDEX",
  "RANGE BREAK 200 INDEX",
  "VOL OVER BOOM 400",
  "VOL OVER BOOM 550",
  "VOL OVER BOOM 750",
  "VOL OVER CRASH 400",
  "VOL OVER CRASH 550",
  "VOL OVER CRASH 750",
  "TREK UP INDEX",
  "TREK DOWN INDEX",
  "BULL MARKET INDEX",
  "BEAR MARKET INDEX",
  "FOREX BASKET",
  "GOLD BASKET",
  "GBP BASKET",
  "EUR BASKET",
  "USD BASKET",
];

const WELTRADE_SYNTHETIC_LIBRARY = [
  "FX VOL 20",
  "FX VOL 21",
  "FX VOL 22",
  "FX VOL 23",
  "FX VOL 24",
  "FX VOL 25",
  "FX VOL 26",
  "FX VOL 27",
  "FX VOL 28",
  "FX VOL 29",
  "FX VOL 30",
  "FX VOL 31",
  "FX VOL 32",
  "FX VOL 33",
  "FX VOL 34",
  "FX VOL 35",
  "FX VOL 36",
  "FX VOL 37",
  "FX VOL 38",
  "FX VOL 39",
  "FX VOL 40",
  "FX VOL 41",
  "FX VOL 42",
  "FX VOL 43",
  "FX VOL 44",
  "FX VOL 45",
  "FX VOL 46",
  "FX VOL 47",
  "FX VOL 48",
  "FX VOL 49",
  "FX VOL 50",
  "FX VOL 51",
  "FX VOL 52",
  "FX VOL 53",
  "FX VOL 54",
  "FX VOL 55",
  "FX VOL 56",
  "FX VOL 57",
  "FX VOL 58",
  "FX VOL 59",
  "FX VOL 60",
  "FX VOL 61",
  "FX VOL 62",
  "FX VOL 63",
  "FX VOL 64",
  "FX VOL 65",
  "FX VOL 66",
  "FX VOL 67",
  "FX VOL 68",
  "FX VOL 69",
  "FX VOL 70",
  "FX VOL 71",
  "FX VOL 72",
  "FX VOL 73",
  "FX VOL 74",
  "FX VOL 75",
  "FX VOL 76",
  "FX VOL 77",
  "FX VOL 78",
  "FX VOL 79",
  "FX VOL 80",
  "FX VOL 81",
  "FX VOL 82",
  "FX VOL 83",
  "FX VOL 84",
  "FX VOL 85",
  "FX VOL 86",
  "FX VOL 87",
  "FX VOL 88",
  "FX VOL 89",
  "FX VOL 90",
  "FX VOL 91",
  "FX VOL 92",
  "FX VOL 93",
  "FX VOL 94",
  "FX VOL 95",
  "FX VOL 96",
  "FX VOL 97",
  "FX VOL 98",
  "FX VOL 99",
  "SFX VOL 20",
  "SFX VOL 21",
  "SFX VOL 22",
  "SFX VOL 23",
  "SFX VOL 24",
  "SFX VOL 25",
  "SFX VOL 26",
  "SFX VOL 27",
  "SFX VOL 28",
  "SFX VOL 29",
  "SFX VOL 30",
  "SFX VOL 31",
  "SFX VOL 32",
  "SFX VOL 33",
  "SFX VOL 34",
  "SFX VOL 35",
  "SFX VOL 36",
  "SFX VOL 37",
  "SFX VOL 38",
  "SFX VOL 39",
  "SFX VOL 40",
  "SFX VOL 41",
  "SFX VOL 42",
  "SFX VOL 43",
  "SFX VOL 44",
  "SFX VOL 45",
  "SFX VOL 46",
  "SFX VOL 47",
  "SFX VOL 48",
  "SFX VOL 49",
  "SFX VOL 50",
  "SFX VOL 51",
  "SFX VOL 52",
  "SFX VOL 53",
  "SFX VOL 54",
  "SFX VOL 55",
  "SFX VOL 56",
  "SFX VOL 57",
  "SFX VOL 58",
  "SFX VOL 59",
  "SFX VOL 60",
  "SFX VOL 61",
  "SFX VOL 62",
  "SFX VOL 63",
  "SFX VOL 64",
  "SFX VOL 65",
  "SFX VOL 66",
  "SFX VOL 67",
  "SFX VOL 68",
  "SFX VOL 69",
  "SFX VOL 70",
  "SFX VOL 71",
  "SFX VOL 72",
  "SFX VOL 73",
  "SFX VOL 74",
  "SFX VOL 75",
  "SFX VOL 76",
  "SFX VOL 77",
  "SFX VOL 78",
  "SFX VOL 79",
  "SFX VOL 80",
  "SFX VOL 81",
  "SFX VOL 82",
  "SFX VOL 83",
  "SFX VOL 84",
  "SFX VOL 85",
  "SFX VOL 86",
  "SFX VOL 87",
  "SFX VOL 88",
  "SFX VOL 89",
  "SFX VOL 90",
  "SFX VOL 91",
  "SFX VOL 92",
  "SFX VOL 93",
  "SFX VOL 94",
  "SFX VOL 95",
  "SFX VOL 96",
  "SFX VOL 97",
  "SFX VOL 98",
  "SFX VOL 99",
  "PAINX 400",
  "PAINX 600",
  "PAINX 800",
  "PAINX 999",
  "PAINX 1200",
  "GAINX 400",
  "GAINX 600",
  "GAINX 800",
  "GAINX 999",
  "GAINX 1200",
  "FLIPX 1",
  "FLIPX 2",
  "FLIPX 3",
  "FLIPX 4",
  "FLIPX 5",
  "SWITCHX 600",
  "SWITCHX 1200",
  "SWITCHX 1800",
  "BREAKX 600",
  "BREAKX 1200",
  "BREAKX 1800",
  "TRENDX 600",
  "TRENDX 1200",
  "TRENDX 1800",
  "PLUSX 1",
  "FIBOX",
  "QUADX",
  "STOCK MARKET INDEX",
  "FINANCIAL MARKET MIRROR",
];

const FOREX_SYMBOL_LIBRARY = [
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "USDCAD",
  "AUDUSD",
  "NZDUSD",
  "EURJPY",
  "GBPJPY",
  "EURGBP",
  "EURAUD",
  "EURCAD",
  "EURCHF",
  "EURNZD",
  "EURSGD",
  "EURNOK",
  "EURSEK",
  "EURDKK",
  "EURHUF",
  "EURMXN",
  "GBPAUD",
  "GBPCAD",
  "GBPCHF",
  "GBPNZD",
  "GBPSGD",
  "AUDCAD",
  "AUDCHF",
  "AUDJPY",
  "AUDNZD",
  "AUDSGD",
  "NZDCAD",
  "NZDCHF",
  "NZDJPY",
  "NZDSGD",
  "CADCHF",
  "CADJPY",
  "CHFJPY",
  "CHFSGD",
  "SGDJPY",
  "USDSGD",
  "USDHKD",
  "USDCNH",
  "USDMXN",
  "USDNOK",
  "USDSEK",
  "USDZAR",
  "USDDKK",
  "USDHUF",
  "USDRUB",
  "USDTRY",
  "EURRUB",
  "EURPLN",
  "USDPLN",
  "GBPSEK",
  "GBPTRY",
  "EURTRY",
];

const METALS_AND_CFD_SYMBOL_LIBRARY = [
  "XAUUSD",
  "GOLD",
  "XAGUSD",
  "SILVER",
  "XPTUSD",
  "XPDUSD",
  "XTIUSD",
  "XBRUSD",
  "BTCUSD",
  "ETHUSD",
  "LTCUSD",
  "XRPUSD",
  "XRPUSDT",
  "BCHUSD",
  "XMRUSD",
  "DASHUSD",
  "ZECUSD",
  "EOSUSD",
  "NEOUSD",
  "BCHBTC",
  "ETHBTC",
  "XMRBTC",
  "DASHBTC",
  "ZECBTC",
  "DJIUSD",
  "NDXUSD",
  "SPXUSD",
  "AEXEUR",
  "AXJAUD",
  "GEREUR",
  "F40EUR",
  "FTSGBP",
  "HSIHKD",
  "IBXEUR",
  "SSMCHF",
  "STXEUR",
  "IT4EUR",
  "JPXJPY",
];

const POPULAR_SYMBOL_LIBRARY = [
  ...DERIV_SYMBOL_LIBRARY,
  ...WELTRADE_SYNTHETIC_LIBRARY,
  ...FOREX_SYMBOL_LIBRARY,
  ...METALS_AND_CFD_SYMBOL_LIBRARY,
];

function detectBrokerFamily(status) {
  const raw = [
    pick(status?.account || {}, ["server", "trade_server", "broker_server", "broker"], ""),
    pick(status || {}, ["broker", "broker_name", "server"], ""),
  ]
    .map((value) => String(value || "").toUpperCase())
    .join(" ");

  if (!raw) return "generic";
  if (raw.includes("WELTRADE")) return "weltrade";
  if (
    raw.includes("DERIV") ||
    raw.includes("BINARY") ||
    raw.includes("SYNTHETIC") ||
    raw.includes("CRASH") ||
    raw.includes("BOOM") ||
    raw.includes("VOLATILITY")
  ) {
    return "deriv";
  }

  return "generic";
}

function getBrokerPrioritizedLibrary(brokerFamily) {
  if (brokerFamily === "deriv") {
    return [
      ...DERIV_SYMBOL_LIBRARY,
      ...FOREX_SYMBOL_LIBRARY,
      ...METALS_AND_CFD_SYMBOL_LIBRARY,
      ...WELTRADE_SYNTHETIC_LIBRARY,
    ];
  }

  if (brokerFamily === "weltrade") {
    return [
      ...WELTRADE_SYNTHETIC_LIBRARY,
      ...FOREX_SYMBOL_LIBRARY,
      ...METALS_AND_CFD_SYMBOL_LIBRARY,
      ...DERIV_SYMBOL_LIBRARY,
    ];
  }

  return POPULAR_SYMBOL_LIBRARY;
}

function getSymbolPriorityScore(symbol, brokerFamily, recentCounts = new Map()) {
  const upper = String(symbol || "").toUpperCase().trim();
  const keys = getSymbolSearchKeys(upper);
  const recentScore = (recentCounts.get(upper) || 0) * 100;

  const isDeriv = keys.some(
    (key) =>
      key.startsWith("VOL") ||
      key.startsWith("V10") ||
      key.startsWith("V25") ||
      key.startsWith("V50") ||
      key.startsWith("V75") ||
      key.startsWith("V90") ||
      key.startsWith("V100") ||
      key.startsWith("C") ||
      key.startsWith("B") ||
      key.startsWith("J") ||
      key.startsWith("DS") ||
      key === "STEPINDEX" ||
      upper.includes("INDEX") ||
      upper.includes("BASKET")
  );

  const isWeltradeSynthetic = /^FX VOL\s+\d+$/i.test(upper);
  const isForex = /^[A-Z]{6}$/.test(upper);
  const isMetalOrCfd = !isForex && !isDeriv && !isWeltradeSynthetic;

  if (brokerFamily === "deriv") {
    if (isDeriv) return 4000 + recentScore;
    if (isForex) return 3000 + recentScore;
    if (isMetalOrCfd) return 2000 + recentScore;
    if (isWeltradeSynthetic) return 1000 + recentScore;
  }

  if (brokerFamily === "weltrade") {
    if (isWeltradeSynthetic) return 4000 + recentScore;
    if (isForex) return 3000 + recentScore;
    if (isMetalOrCfd) return 2000 + recentScore;
    if (isDeriv) return 1000 + recentScore;
  }

  return recentScore;
}

function normalizeSymbolSearch(value) {
  return String(value || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9]/g, "");
}

function getSymbolSearchKeys(symbol) {
  const upper = String(symbol || "").toUpperCase().trim();
  const keys = new Set([normalizeSymbolSearch(upper)]);

  const noIndex = upper.replace(/\s+INDEX$/, "").trim();
  if (noIndex) keys.add(normalizeSymbolSearch(noIndex));

  const volatilityMatch = upper.match(/^VOLATILITY\s+(\d+)(?:\s+\(1S\))?\s+INDEX$/);
  if (volatilityMatch) {
    const suffix = /\(1S\)/.test(upper) ? "1S" : "";
    keys.add(`VOL${volatilityMatch[1]}${suffix}`);
    keys.add(`V${volatilityMatch[1]}${suffix}`);
  }

  const spikeMatch = upper.match(/^(CRASH|BOOM|JUMP)\s+(\d+)\s+INDEX$/);
  if (spikeMatch) {
    keys.add(`${spikeMatch[1][0]}${spikeMatch[2]}`);
  }

  const driftMatch = upper.match(/^DRIFT SWITCHING INDEX\s+(\d+)$/);
  if (driftMatch) {
    keys.add(`DS${driftMatch[1]}`);
    keys.add(`DSI${driftMatch[1]}`);
  }

  const dexMatch = upper.match(/^DEX\s+(\d+)\s+(UP|DN)$/);
  if (dexMatch) {
    keys.add(`DEX${dexMatch[1]}${dexMatch[2]}`);
  }

  const rangeBreakMatch = upper.match(/^RANGE BREAK\s+(\d+)\s+INDEX$/);
  if (rangeBreakMatch) {
    keys.add(`RB${rangeBreakMatch[1]}`);
    keys.add(`RANGEBREAK${rangeBreakMatch[1]}`);
  }

  const hybridMatch = upper.match(/^VOL OVER (BOOM|CRASH)\s+(\d+)$/);
  if (hybridMatch) {
    keys.add(`VO${hybridMatch[1][0]}${hybridMatch[2]}`);
    keys.add(`VO${hybridMatch[1].slice(0, 2)}${hybridMatch[2]}`);
  }

  const stepMatch = upper.match(/^STEP\s+(\d+)\s+INDEX$/);
  if (stepMatch) {
    keys.add(`STEP${stepMatch[1]}`);
  }

  if (upper === "STEP INDEX") {
    keys.add("STEP");
  }

  const multiStepMatch = upper.match(/^MULTI STEP\s+(\d+)\s+INDEX$/);
  if (multiStepMatch) {
    keys.add(`MSTEP${multiStepMatch[1]}`);
    keys.add(`MULTISTEP${multiStepMatch[1]}`);
  }

  const skewStepMatch = upper.match(/^SKEW STEP\s+(\d+)\s+(UP|DOWN)\s+INDEX$/);
  if (skewStepMatch) {
    keys.add(`SKEWSTEP${skewStepMatch[1]}${skewStepMatch[2]}`);
    keys.add(`SS${skewStepMatch[1]}${skewStepMatch[2]}`);
  }

  const fxVolMatch = upper.match(/^(S?FX VOL)\s+(\d+)$/);
  if (fxVolMatch) {
    const prefix = fxVolMatch[1].startsWith("S") ? "SFXVOL" : "FXVOL";
    keys.add(`${prefix}${fxVolMatch[2]}`);
    keys.add(`${fxVolMatch[1].startsWith("S") ? "SFV" : "FV"}${fxVolMatch[2]}`);
  }

  const familyNumericMatch = upper.match(/^(PAINX|GAINX|FLIPX|SWITCHX|BREAKX|TRENDX|PLUSX)\s+(\d+)$/);
  if (familyNumericMatch) {
    keys.add(`${familyNumericMatch[1]}${familyNumericMatch[2]}`);
  }

  if (/^(FIBOX|QUADX|STOCK MARKET INDEX|FINANCIAL MARKET MIRROR)$/.test(upper)) {
    keys.add(normalizeSymbolSearch(upper.replace(/\s+/g, "")));
  }

  const pairMatch = upper.match(/^([A-Z]{3})([A-Z]{3})$/);
  if (pairMatch) {
    keys.add(normalizeSymbolSearch(`${pairMatch[1]}/${pairMatch[2]}`));
    keys.add(normalizeSymbolSearch(`${pairMatch[1]}-${pairMatch[2]}`));
  }

  return Array.from(keys).filter(Boolean);
}

function countOccurrences(haystack, needle) {
  if (!haystack || !needle) return 0;
  let count = 0;
  let idx = 0;
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count += 1;
    idx += needle.length;
  }
  return count;
}

function buildStorageSymbolCounts(symbolPool) {
  if (typeof window === "undefined") return new Map();

  const buckets = [];
  const shouldInspectKey = (key) => /journal|trade|execution|history|analysis|chat|note|market|symbol|position/i.test(String(key || ""));

  const readStorage = (storageLike) => {
    try {
      if (!storageLike) return;
      for (let i = 0; i < storageLike.length; i += 1) {
        const key = storageLike.key(i);
        const value = storageLike.getItem(key);
        if (!value) continue;
        if (!shouldInspectKey(key) && String(value).length > 15000) continue;
        buckets.push(String(value).slice(0, 20000));
      }
    } catch {}
  };

  readStorage(window.localStorage);
  readStorage(window.sessionStorage);

  const corpus = normalizeSymbolSearch(buckets.join(" "));
  if (!corpus) return new Map();

  const counts = new Map();
  const uniquePool = Array.from(new Set((Array.isArray(symbolPool) ? symbolPool : []).map((item) => String(item || "").toUpperCase().trim()).filter(Boolean)));

  uniquePool.forEach((symbol) => {
    const score = getSymbolSearchKeys(symbol)
      .filter((key) => key.length >= 4)
      .reduce((sum, key) => sum + countOccurrences(corpus, key), 0);

    if (score > 0) counts.set(symbol, score);
  });

  return counts;
}



function isAgentReallyConnected(status) {
  const hb =
    status?.last_heartbeat ??
    status?.lastHeartbeat ??
    status?.account?.last_heartbeat ??
    null;

  const explicit = pick(status || {}, ["connected", "is_connected", "online", "active"], null);
  const d = hb ? toDate(hb) : null;
  const heartbeatAlive = !!d && Date.now() - d.getTime() < 20000;

  if (explicit === false) return false;
  if (explicit === true && heartbeatAlive) return true;
  return heartbeatAlive;
}

function ExecutionRequirementCard({ hasToken, isConnected }) {
  const { t } = useTranslation();
  if (hasToken && isConnected) return null;

  return (
    <div className="mb-4 rounded-[24px] border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-sm text-amber-800  dark:border-amber-400/35 dark:bg-amber-400/10 dark:text-amber-100">
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
        <div className="space-y-2">
          <div className="font-semibold">{t("execution.requirement.title")}</div>
          <div className="leading-6">
            {t("execution.requirement.body")}
          </div>
        </div>
      </div>
    </div>
  );
}

const EXEC_TABS = [
  { key: "market", icon: Play, labelKey: "execution.tabs.market" },
  { key: "pending", icon: Clock, labelKey: "execution.tabs.pending" },
  { key: "modify", icon: Pencil, labelKey: "execution.tabs.modify" },
  { key: "close", icon: XCircle, labelKey: "execution.tabs.close" },
  { key: "be", icon: MoveRight, labelKey: "execution.tabs.be" },
  { key: "trailing", icon: GitBranchPlus, labelKey: "execution.tabs.trailing" },
  { key: "rr", icon: Target, labelKey: "execution.tabs.rr" },
  { key: "builder", icon: Layers3, labelKey: "execution.tabs.builder" },
];

// L'en-tête (titre, statuts, balance/equity/serveur) est une section à part
// entière sur mobile : il remplissait l'écran, donc choisir « Pending » ne
// montrait rien. Il devient une entrée du sous-menu, comme les autres.
const OVERVIEW_KEY = "overview";
const EXEC_NAV_ITEMS = [{ key: OVERVIEW_KEY, icon: Activity, labelKey: "execution.tabs.overview" }, ...EXEC_TABS];

// Sous-menu des types d'exécution.
// Sous lg il n'est pas rendu ici : le shell l'expose via le bouton à droite de la
// barre du haut, qui l'ouvre en drawer venant de la droite (voir usePageNav).
// À partir de lg : colonne classique à côté du contenu.
function ExecTabSidebar({ execTab, setExecTab, open, setOpen }) {
  const { t } = useTranslation();
  return (
    <aside
      className={[
        "hidden shrink-0 overflow-hidden border-slate-200 dark:bg-[#0f1729] dark:border-[#26314a] bg-white",
        "lg:block lg:h-full lg:w-auto lg:border-r lg:transition-[width] lg:duration-300 lg:ease-in-out",
        open ? "lg:w-[232px] lg:p-3" : "lg:w-14 lg:p-2",
      ].join(" ")}
    >
      <div className="flex h-full flex-col">
        <div className={`mb-3 flex ${open ? "items-center justify-between gap-2" : "justify-center"}`}>
          {open && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-300">
              {t("execution.sidebar.heading")}
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            title={open ? t("execution.sidebar.collapse") : t("execution.sidebar.expand")}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 dark:border-[#26314a] dark:bg-[#0f1729] dark:text-slate-200 dark:hover:bg-[#141d33]"
          >
            {open ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        <div
          className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-auto"
          role="tablist"
          aria-label={t("execution.sidebar.tabsAria")}
        >
          {EXEC_TABS.map((tab) => {
            const active = execTab === tab.key;
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setExecTab(tab.key)}
                title={t(tab.labelKey)}
                className={[
                  "inline-flex shrink-0 items-center rounded-xl border text-sm font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-500/45",
                  open ? "h-11 justify-start gap-2.5 px-3" : "mx-auto h-11 w-10 justify-center",
                  active
                    ? "border-violet-600 bg-violet-600 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-[#26314a] dark:bg-[#0f1729] dark:text-slate-200 dark:hover:bg-[#141d33]",
                ].join(" ")}
              >
                <Icon size={16} className={active ? "text-white" : "text-slate-500 dark:text-slate-300"} />
                {open && <span className="truncate">{t(tab.labelKey)}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function normalizeOpenTrade(t) {
  return {
    kind: pick(t, ["kind", "type"], "position"),
    ticket: String(pick(t, ["ticket", "id", "cmd_id", "trade_id"], "-")),
    symbol: String(pick(t, ["symbol"], "-")).toUpperCase(),
    side: String(pick(t, ["side"], "-")).toLowerCase(),
    order_type: pick(t, ["order_type"], undefined),
    volume: toNumber(pick(t, ["volume"], 0)),
    open_price: toNumber(pick(t, ["open_price", "price", "price_open"], 0)),
    current_price: toNumber(pick(t, ["current_price", "price_current", "market_price"], 0)),
    profit: toNumber(pick(t, ["profit", "pnl", "pl"], 0)),
    open_time: pick(t, ["open_time", "opened_at", "time", "created_at"], null),
    sl: pick(t, ["sl"], null),
    tp: pick(t, ["tp"], null),
  };
}

function normalizeHistoryTrade(t) {
  return {
    ticket: String(pick(t, ["ticket", "id", "cmd_id", "trade_id"], "-")),
    symbol: String(pick(t, ["symbol"], "-")).toUpperCase(),
    side: String(pick(t, ["side"], "-")).toLowerCase(),
    volume: toNumber(pick(t, ["volume"], 0)),
    open_price: toNumber(pick(t, ["open_price", "price"], 0)),
    close_price: toNumber(pick(t, ["close_price"], 0)),
    profit: toNumber(pick(t, ["profit"], 0)),
    open_time: pick(t, ["open_time", "opened_at"], null),
    close_time: pick(t, ["close_time", "closed_at"], null),
    status: pick(t, ["status"], ""),
  };
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-2.5">
      <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
        {label}
      </label>
      {children}
      {hint ? (
        <p className="text-[11px] leading-5 text-slate-500 dark:text-slate-300">{hint}</p>
      ) : null}
    </div>
  );
}

function Chip({ children, tone = "neutral" }) {
  const tones = {
    neutral:
      "border-slate-200/80 bg-white/80 text-slate-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200",
    violet:
      "border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300",
    emerald:
      "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    rose: "border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-300",
    amber:
      "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    sky: "border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3 sm:mb-5">
      <div className="min-w-0">
        <h3 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white sm:text-lg">
          {title}
        </h3>
        {subtitle ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">{subtitle}</p>
        ) : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "violet" }) {
  const tones = {
    violet:
      "text-violet-700 ring-violet-500/20 bg-violet-500/10 dark:text-violet-300",
    emerald:
      "text-emerald-700 ring-emerald-500/20 bg-emerald-500/10 dark:text-emerald-300",
    rose: "text-rose-700 ring-rose-500/20 bg-rose-500/10 dark:text-rose-300",
    amber:
      "text-amber-700 ring-amber-500/20 bg-amber-500/10 dark:text-amber-300",
    sky: "text-sky-700 ring-sky-500/20 bg-sky-500/10 dark:text-sky-300",
  };

  return (
    <div className={`${cardClass} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
            {label}
          </div>
          <div className="mt-2 truncate text-lg font-semibold text-slate-950 dark:text-white">
            {value}
          </div>
        </div>
        <span
          className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ring-1 ${tones[tone] || tones.violet}`}
        >
          <Icon size={17} />
        </span>
      </div>
    </div>
  );
}

function TabButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={[
        "group relative inline-flex h-12 shrink-0 items-center justify-center gap-2.5 rounded-xl border px-4 text-sm font-semibold whitespace-nowrap transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-0",
        active
          ? "border-violet-600 bg-violet-600 text-white"
          : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:border-[#26314a] dark:bg-[#0f1729] dark:text-slate-100 dark:hover:bg-[#141d33]",
      ].join(" ")}
    >
      <Icon
        size={16}
        className={active ? "text-white" : "text-slate-500 dark:text-slate-300"}
      />
      <span className={active ? "text-white" : "text-slate-800 dark:text-slate-100"}>{label}</span>
    </button>
  );
}

function QuickPreset({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-white dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200 dark:hover:bg-white/[0.09]"
    >
      {label}
    </button>
  );
}

function SymbolInput({ value, onChange, options, placeholder }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const source = useMemo(() => {
    const items = Array.isArray(options) ? options : [];
    return Array.from(new Set(items.map((item) => String(item || "").toUpperCase().trim()).filter(Boolean)));
  }, [options]);

  const filteredOptions = useMemo(() => {
    const qRaw = String(value || "").trim().toUpperCase();
    const q = normalizeSymbolSearch(qRaw);

    if (!q) return source.slice(0, 80);

    return source
      .map((item, index) => {
        const raw = String(item || "").toUpperCase();
        const compact = normalizeSymbolSearch(raw);
        const keys = getSymbolSearchKeys(raw);
        let score = 0;

        if (raw === qRaw || compact === q || keys.includes(q)) score = 100;
        else if (raw.startsWith(qRaw)) score = 90;
        else if (keys.some((key) => key.startsWith(q))) score = 80;
        else if (raw.includes(qRaw)) score = 70;
        else if (keys.some((key) => key.includes(q))) score = 60;

        return { item, score, index };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .slice(0, 80)
      .map((entry) => entry.item);
  }, [source, value]);

  const showTypedValue = useMemo(() => {
    const qRaw = String(value || "").trim().toUpperCase();
    const q = normalizeSymbolSearch(qRaw);
    if (!qRaw) return false;
    return !source.some((item) => normalizeSymbolSearch(item) === q);
  }, [source, value]);

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search
          size={15}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-400"
        />
        <input
          value={value}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            onChange(String(e.target.value || "").toUpperCase());
            setOpen(true);
          }}
          placeholder={placeholder || t("execution.symbolInput.placeholder")}
          autoComplete="off"
          className="h-12 w-full rounded-2xl border border-slate-200/80 bg-white/95 pl-10 pr-10 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-violet-400/70 focus:ring-4 focus:ring-violet-500/10 dark:border-white/[0.10] dark:bg-[#0b1120] dark:text-white dark:placeholder:text-white/28"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-xl text-slate-500 transition hover:bg-black/5 dark:text-slate-300 dark:hover:bg-white/10"
        >
          <ChevronDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
      </div>

      {open ? (
        <div className="absolute z-[80] mt-2 max-h-72 w-full overflow-y-auto overflow-x-hidden rounded-2xl border border-slate-200/90 bg-white p-2  ring-1 ring-black/5 dark:border-violet-400/20 dark:bg-[#0a1020] dark: dark:ring-white/5">
          <div className="grid gap-1">
            {showTypedValue ? (
              <button
                type="button"
                onClick={() => {
                  onChange(String(value || "").toUpperCase().trim());
                  setOpen(false);
                }}
                className="rounded-xl border border-dashed border-violet-400/30 bg-violet-500/6 px-3 py-2.5 text-left text-sm font-medium text-violet-700 transition hover:bg-violet-500/10 dark:text-violet-300"
              >
                {t("execution.symbolInput.use", { value: String(value || "").toUpperCase().trim() })}
              </button>
            ) : null}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((symbol) => (
                <button
                  key={symbol}
                  type="button"
                  onClick={() => {
                    onChange(symbol);
                    setOpen(false);
                  }}
                  className={[
                    "rounded-xl border px-3 py-2.5 text-left text-sm transition",
                    value === symbol
                      ? "border-violet-500/30 bg-violet-600 text-white "
                      : "border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-100 dark:text-white dark:hover:border-white/[0.08] dark:hover:bg-white/[0.06]",
                  ].join(" ")}
                >
                  {symbol}
                </button>
              ))
            ) : !showTypedValue ? (
              <div className="px-3 py-2.5 text-sm text-slate-500 dark:text-slate-300">
                {t("execution.symbolInput.empty")}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LotCenterControl({ value, onChange }) {
  const { t } = useTranslation();
  const current = Math.max(0.01, nfloat(value) ?? 0.1);

  const setNext = (next) => {
    const safe = Math.max(0.01, +Number(next).toFixed(2));
    onChange(String(safe));
  };

  return (
    <div className="flex h-[60px] w-[124px] shrink-0 flex-col justify-center rounded-[22px] border border-slate-200/80 bg-white/95 px-2  dark:border-white/10 dark:bg-[#0f1529] sm:h-[66px] sm:w-[140px]">
      <div className="text-center text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {t("execution.lot")}
      </div>
      <div className="mt-1 flex items-center justify-center gap-1">
        <button
          type="button"
          onClick={() => setNext(current - 0.01)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200/80 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <Minus size={13} />
        </button>
        <input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-[48px] rounded-xl border border-slate-200/80 bg-white text-center text-sm font-semibold text-slate-900 outline-none focus:border-violet-400/70 dark:border-white/10 dark:bg-[#0b1326] dark:text-white sm:w-[56px]"
        />
        <button
          type="button"
          onClick={() => setNext(current + 0.01)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200/80 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}

function HorizontalTradeCard({ item, pending = false }) {
  const { t } = useTranslation();
  const isBuy = item.side === "buy";
  const tone = pending
    ? "bg-violet-500/10 text-violet-700 ring-violet-500/20 dark:text-violet-300"
    : isBuy
    ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300"
    : "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300";

  const typeLabel = pending
    ? String(item.order_type || "PENDING").replaceAll("_", " ").toUpperCase()
    : isBuy
    ? "BUY"
    : "SELL";

  return (
    <div className="w-[290px] flex-none rounded-[22px] border border-slate-200/70 bg-white/88 p-4  dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            {item.symbol}
          </div>
          <div className="mt-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">
            #{item.ticket}
          </div>
        </div>
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${tone}`}>
          {typeLabel}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.tradeCard.vol")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.volume}
          </div>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.tradeCard.price")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.open_price || "—"}
          </div>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.tradeCard.sl")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.sl ?? "—"}
          </div>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.tradeCard.tp")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.tp ?? "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCellNumber(value, digits = 2) {
  if (value == null || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatPriceCell(value) {
  if (value == null || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 5,
  });
}

function formatCompactDate(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sideTone(side) {
  return String(side || "").toLowerCase() === "buy"
    ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300"
    : "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300";
}

function OpenPositionsTable({ items }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-[24px] border border-emerald-500/16 bg-white/88 dark:border-emerald-400/12 dark:bg-[#0d1424]/92">
      <div className="hidden grid-cols-[170px_140px_120px_100px_180px_140px_120px_120px_130px] gap-0 border-b border-slate-200/70 px-4 py-4 dark:border-white/10 lg:grid">
        {[
          t("execution.positions.headTicket"),
          t("execution.positions.headSymbol"),
          t("execution.positions.headSide"),
          t("execution.positions.headVol"),
          t("execution.positions.headOpen"),
          t("execution.positions.headPriceOpen"),
          t("execution.positions.headSl"),
          t("execution.positions.headTp"),
          t("execution.positions.headPnl"),
        ].map((head) => (
          <div
            key={head}
            className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300"
          >
            {head}
          </div>
        ))}
      </div>

      {items.length ? (
        <>
          <div className="divide-y divide-slate-200/70 dark:divide-white/10 lg:block hidden">
            {items.map((item) => {
              const positive = toNumber(item.profit, 0) >= 0;
              return (
                <div
                  key={`${item.ticket}-${item.symbol}`}
                  className="grid grid-cols-[170px_140px_120px_100px_180px_140px_120px_120px_130px] items-center px-4 py-4 text-sm text-slate-700 dark:text-slate-200"
                >
                  <div className="truncate font-mono text-xs text-slate-600 dark:text-slate-300">#{item.ticket}</div>
                  <div className="truncate font-semibold text-slate-950 dark:text-white">{item.symbol || "—"}</div>
                  <div>
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ring-1 ${sideTone(item.side)}`}>
                      {item.side || "—"}
                    </span>
                  </div>
                  <div>{formatCellNumber(item.volume, 2)}</div>
                  <div>{formatCompactDate(item.open_time)}</div>
                  <div>{formatPriceCell(item.open_price)}</div>
                  <div>{formatPriceCell(item.sl)}</div>
                  <div>{formatPriceCell(item.tp)}</div>
                  <div>
                    <span
                      className={[
                        "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                        positive
                          ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300"
                          : "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
                      ].join(" ")}
                    >
                      {positive ? "+" : ""}
                      {formatCellNumber(item.profit, 2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid gap-3 p-3 lg:hidden">
            {items.map((item) => {
              const positive = toNumber(item.profit, 0) >= 0;
              return (
                <div
                  key={`${item.ticket}-${item.symbol}`}
                  className="rounded-[20px] border border-slate-200/70 bg-white/94 p-4 dark:border-white/10 dark:bg-white/[0.04]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-950 dark:text-white">{item.symbol || "—"}</div>
                      <div className="mt-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">#{item.ticket}</div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ring-1 ${sideTone(item.side)}`}>
                        {item.side || "—"}
                      </span>
                      <span
                        className={[
                          "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                          positive
                            ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300"
                            : "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
                        ].join(" ")}
                      >
                        {positive ? "+" : ""}
                        {formatCellNumber(item.profit, 2)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]"><div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.positions.volume")}</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{formatCellNumber(item.volume, 2)}</div></div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]"><div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.positions.open")}</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{formatCompactDate(item.open_time)}</div></div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]"><div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.positions.priceOpen")}</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{formatPriceCell(item.open_price)}</div></div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]"><div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.positions.sl")}</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{formatPriceCell(item.sl)}</div></div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]"><div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.positions.tp")}</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{formatPriceCell(item.tp)}</div></div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]"><div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.positions.pnl")}</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{positive ? "+" : ""}{formatCellNumber(item.profit, 2)}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex min-h-[108px] items-center justify-center px-4 py-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/8 px-5 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-current text-[11px]">✓</span>
            {t("execution.positions.empty")}
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryCard({ item }) {
  const { t } = useTranslation();

  const positive = toNumber(item.profit, 0) >= 0;

  return (
    <div className="rounded-[22px] border border-slate-200/70 bg-white/86 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            {item.symbol}
          </div>
          <div className="mt-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">
            #{item.ticket}
          </div>
        </div>
        <span
          className={[
            "inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1",
            positive
              ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300"
              : "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
          ].join(" ")}
        >
          {positive ? "+" : ""}
          {formatNumber(item.profit)}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.history.side")}</div>
          <div className="mt-1 text-sm font-semibold uppercase text-slate-900 dark:text-white">
            {item.side || "—"}
          </div>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.history.volume")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.volume}
          </div>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.history.open")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.open_price || "—"}
          </div>
        </div>
        <div className="rounded-2xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.04]">
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.history.close")}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
            {item.close_price || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExecutionPage() {
  const { t } = useTranslation();
  const [agentToken, setAgentToken] = useState(() => (storage.get(LS_TOKEN) || "").trim());

  const [status, setStatus] = useState(null);
  const [openPositions, setOpenPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [historyTrades, setHistoryTrades] = useState([]);
  const [sendingExec, setSendingExec] = useState(false);
  const isDesktop = useIsDesktop();
  // Mobile : on arrive sur l'aperçu. Desktop : l'en-tête est toujours affiché
  // au-dessus du formulaire, donc on ouvre directement sur Market.
  const [execTab, setExecTab] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 1024 ? OVERVIEW_KEY : "market"
  );

  // Sur desktop l'aperçu n'est pas une section : si on y arrive (passage mobile
  // -> desktop), on retombe sur Market.
  useEffect(() => {
    if (isDesktop && execTab === OVERVIEW_KEY) setExecTab("market");
  }, [isDesktop, execTab]);

  // Mobile : le shell expose ces onglets via le bouton à droite de la barre du
  // haut, qui les ouvre en drawer venant de la droite.
  usePageNav({ items: EXEC_NAV_ITEMS, activeKey: execTab, onSelect: setExecTab, title: t("execution.pageTitle") });
  const [tabOpen, setTabOpen] = useState(true);
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");
  const refreshTimersRef = useRef([]);

  // Exclusion mutuelle avec la nav principale : déplier la sidebar Exécution plie la nav.
  // Au montage on ne touche à rien : la nav principale doit rester dépliée à
  // chaque actualisation, on ne la plie qu'après une action de l'utilisateur.
  const navExclusionArmed = useRef(false);
  const tabOpenAtMount = useRef(tabOpen);
  useEffect(() => {
    if (!navExclusionArmed.current) {
      // Tant que l'état n'a pas bougé depuis le montage, on ne fait rien
      // (compatible double-montage StrictMode en dev).
      if (tabOpen === tabOpenAtMount.current) return;
      navExclusionArmed.current = true;
    }
    if (tabOpen) {
      try { localStorage.setItem("fm:sidebar-collapsed", "true"); } catch {}
      window.dispatchEvent(new Event("fm:sidebar-toggle"));
    }
  }, [tabOpen]);

  // Si la nav se déplie, on plie la sidebar Exécution.
  useEffect(() => {
    const onNavToggle = () => {
      let collapsed = true;
      try { collapsed = localStorage.getItem("fm:sidebar-collapsed") !== "false"; } catch {}
      if (!collapsed) setTabOpen(false);
    };
    window.addEventListener("fm:sidebar-toggle", onNavToggle);
    return () => window.removeEventListener("fm:sidebar-toggle", onNavToggle);
  }, []);

  useEffect(() => {
    const syncToken = () => setAgentToken((storage.get(LS_TOKEN) || "").trim());
    syncToken();
    window.addEventListener("storage", syncToken);
    window.addEventListener("focus", syncToken);
    document.addEventListener("visibilitychange", syncToken);

    return () => {
      window.removeEventListener("storage", syncToken);
      window.removeEventListener("focus", syncToken);
      document.removeEventListener("visibilitychange", syncToken);
    };
  }, []);

  const brokerFamily = useMemo(() => detectBrokerFamily(status), [status]);

  const symbolPreferenceCounts = useMemo(() => {
    const counts = new Map();

    const addWeight = (symbol, weight) => {
      const upper = String(symbol || "").toUpperCase().trim();
      if (!upper || upper === "-") return;
      counts.set(upper, (counts.get(upper) || 0) + weight);
    };

    historyTrades.forEach((item, index) => {
      addWeight(item.symbol, Math.max(2, 10 - index));
    });

    openPositions.forEach((item) => addWeight(item.symbol, 14));
    pendingOrders.forEach((item) => addWeight(item.symbol, 10));

    const journalCounts = buildStorageSymbolCounts([
      ...POPULAR_SYMBOL_LIBRARY,
      ...historyTrades.map((item) => item.symbol),
      ...openPositions.map((item) => item.symbol),
      ...pendingOrders.map((item) => item.symbol),
    ]);

    journalCounts.forEach((value, symbol) => addWeight(symbol, value * 6));

    return counts;
  }, [historyTrades, openPositions, pendingOrders]);

  const symbolOptions = useMemo(() => {
    const recentSymbols = [];

    historyTrades.forEach((item) => {
      const symbol = String(item.symbol || "").toUpperCase().trim();
      if (symbol && symbol !== "-") recentSymbols.push(symbol);
    });

    openPositions.forEach((item) => {
      const symbol = String(item.symbol || "").toUpperCase().trim();
      if (symbol && symbol !== "-") recentSymbols.push(symbol);
    });

    pendingOrders.forEach((item) => {
      const symbol = String(item.symbol || "").toUpperCase().trim();
      if (symbol && symbol !== "-") recentSymbols.push(symbol);
    });

    const brokerFirstLibrary = getBrokerPrioritizedLibrary(brokerFamily);
    const merged = [...recentSymbols, ...brokerFirstLibrary, ...POPULAR_SYMBOL_LIBRARY];

    return Array.from(
      new Set(merged.map((item) => String(item || "").toUpperCase().trim()).filter(Boolean))
    ).sort((a, b) => {
      const scoreDiff =
        getSymbolPriorityScore(b, brokerFamily, symbolPreferenceCounts) -
        getSymbolPriorityScore(a, brokerFamily, symbolPreferenceCounts);

      if (scoreDiff !== 0) return scoreDiff;

      return a.localeCompare(b);
    });
  }, [historyTrades, openPositions, pendingOrders, brokerFamily, symbolPreferenceCounts]);

  const defaultSymbol = useMemo(() => {
    return String(symbolOptions[0] || "").toUpperCase();
  }, [symbolOptions]);

  const [formMarket, setFormMarket] = useState({
    symbol: "",
    volume: 0.1,
    sl_tp_mode: "points",
    sl_points: "",
    tp_points: "",
    sl_price: "",
    tp_price: "",
  });

  const [formPending, setFormPending] = useState({
    type: "buy_limit",
    symbol: "",
    volume: 0.1,
    price: "",
    sl_points: "",
    tp_points: "",
  });

  const [formModify, setFormModify] = useState({
    scope: "symbol",
    symbol: "",
    unit: "points",
    sl_points: "",
    tp_points: "",
    sl_price: "",
    tp_price: "",
  });

  const [formClose, setFormClose] = useState({
    symbol: "",
  });

  const [formBE, setFormBE] = useState({
    scope: "symbol",
    symbol: "",
    offset_points: 0,
    only_in_profit: true,
  });

  const [formTrail, setFormTrail] = useState({
    scope: "symbol",
    symbol: "",
    points: 100,
  });

  const [toolRR, setToolRR] = useState({
    side: "buy",
    entry: "",
    stop: "",
    target: "",
  });

  const [toolBuilder, setToolBuilder] = useState({
    side: "buy",
    entry: "",
    slPoints: 100,
    tpPoints: 200,
    pointSize: "0.0001",
  });

  useEffect(() => {
    setFormMarket((f) => ({ ...f, symbol: f.symbol || defaultSymbol }));
    setFormPending((f) => ({ ...f, symbol: f.symbol || defaultSymbol }));
    setFormModify((f) => ({ ...f, symbol: f.symbol || defaultSymbol }));
    setFormClose((f) => ({ ...f, symbol: f.symbol || defaultSymbol }));
    setFormBE((f) => ({ ...f, symbol: f.symbol || defaultSymbol }));
    setFormTrail((f) => ({ ...f, symbol: f.symbol || defaultSymbol }));
  }, [defaultSymbol]);

  const normalizeExecPayload = useCallback(
    (payload) => {
      const next = { ...payload };

      switch (payload.action) {
        case "market_order":
          next.cmd = "market";
          break;
        case "pending_order":
          next.cmd = "pending";
          next.order_type = payload.type;
          delete next.type;
          break;
        case "modify_sl_tp": {
          next.cmd = "modify";
          next.symbol =
            payload.scope === "all"
              ? "ALL"
              : String(payload.symbol || defaultSymbol).toUpperCase();

          const usePriceMode = payload.scope === "all" || payload.unit === "price";

          if (usePriceMode) {
            next.sl = nfloat(payload.sl_price);
            next.tp = nfloat(payload.tp_price);
            next.sl_price = next.sl;
            next.tp_price = next.tp;
            delete next.sl_points;
            delete next.tp_points;
          } else {
            next.sl_points = nfloat(payload.sl_points);
            next.tp_points = nfloat(payload.tp_points);
            delete next.sl;
            delete next.tp;
            delete next.sl_price;
            delete next.tp_price;
          }

          delete next.scope;
          delete next.unit;
          break;
        }
        case "close_symbol":
          next.cmd = "close";
          next.symbol = String(payload.symbol || defaultSymbol).toUpperCase();
          break;
        case "close_all":
          next.cmd = "close_all";
          break;
        case "set_break_even":
          next.cmd = "be";
          next.symbol =
            payload.scope === "all"
              ? "ALL"
              : String(payload.symbol || defaultSymbol).toUpperCase();
          delete next.scope;
          break;
        case "enable_trailing":
          next.cmd = "trailing";
          next.enable = true;
          next.symbol =
            payload.scope === "all"
              ? "ALL"
              : String(payload.symbol || defaultSymbol).toUpperCase();
          delete next.scope;
          break;
        case "disable_trailing":
          next.cmd = "trailing";
          next.enable = false;
          next.symbol =
            payload.scope === "all"
              ? "ALL"
              : String(payload.symbol || defaultSymbol).toUpperCase();
          delete next.scope;
          break;
        default:
          break;
      }

      if (typeof next.symbol === "string" && next.symbol !== "ALL") {
        next.symbol = next.symbol.toUpperCase();
      }

      if (next.price !== undefined) next.price = nfloat(next.price);
      if (next.sl_points !== undefined) next.sl_points = nfloat(next.sl_points);
      if (next.tp_points !== undefined) next.tp_points = nfloat(next.tp_points);
      if (next.sl !== undefined) next.sl = nfloat(next.sl);
      if (next.tp !== undefined) next.tp = nfloat(next.tp);
      if (next.sl_price !== undefined) next.sl_price = nfloat(next.sl_price);
      if (next.tp_price !== undefined) next.tp_price = nfloat(next.tp_price);
      if (next.volume !== undefined) next.volume = nfloat(next.volume) ?? next.volume;
      if (next.offset_points !== undefined) next.offset_points = Number(next.offset_points || 0);
      if (next.points !== undefined) next.points = Number(next.points || 0);

      Object.keys(next).forEach((key) => {
        if (next[key] === undefined || Number.isNaN(next[key])) delete next[key];
      });

      return next;
    },
    [defaultSymbol]
  );

  const splitOpenResponse = useCallback((openResp) => {
    const ensureArr = (x) => (Array.isArray(x) ? x : []);
    if (!openResp) return { positions: [], pendings: [] };

    const arrA = ensureArr(openResp.open_trades);
    const arrB = Array.isArray(openResp) ? openResp : [];
    const pendA = ensureArr(openResp.pending_orders);

    const merged = [...arrA, ...arrB].map(normalizeOpenTrade);

    const positions = merged.filter(
      (x) => (x.kind || "position") === "position" && !x.order_type
    );

    const pendingsExplicit = pendA.map(normalizeOpenTrade);
    const pendingsGuessed = merged.filter((x) => x.order_type || x.kind === "pending");

    const pendings = [...pendingsExplicit, ...pendingsGuessed].reduce((acc, it) => {
      const exists = acc.find((a) => String(a.ticket) === String(it.ticket));
      if (!exists) acc.push(it);
      return acc;
    }, []);

    return { positions, pendings };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!agentToken) {
      setStatus(null);
      setOpenPositions([]);
      setPendingOrders([]);
      setHistoryTrades([]);
      return;
    }

    try {
      setErr("");
      await apiAgentRegisterExisting(agentToken);

      const [s, open, hist] = await Promise.all([
        apiAgentStatus(agentToken),
        apiAgentOpenTrades(agentToken),
        apiAgentHistory(agentToken).catch(() => ({ history: [] })),
      ]);

      setStatus(s || null);

      const { positions, pendings } = splitOpenResponse(open);
      setOpenPositions(positions);
      setPendingOrders(pendings);

      const rawHist = Array.isArray(hist?.history)
        ? hist.history
        : Array.isArray(hist)
        ? hist
        : [];

      setHistoryTrades(rawHist.map(normalizeHistoryTrade));
    } catch (e) {
      setErr(t("execution.errors.fetch", { error: e?.message || e }));
    }
  }, [agentToken, splitOpenResponse]);

  const scheduleRefreshSequence = useCallback(() => {
    refreshTimersRef.current.forEach((timer) => clearTimeout(timer));
    refreshTimersRef.current = [450, 1300, 2600, 4500].map((delay) =>
      window.setTimeout(() => {
        fetchAll();
      }, delay)
    );
  }, [fetchAll]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!agentToken) return undefined;
    const interval = window.setInterval(fetchAll, 4000);
    return () => window.clearInterval(interval);
  }, [agentToken, fetchAll]);

  useEffect(() => {
    return () => {
      refreshTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const removeClosedFromUi = useCallback((payload) => {
    if (payload.action === "close_symbol") {
      const symbol = String(payload.symbol || "").toUpperCase();
      setOpenPositions((prev) =>
        prev.filter((item) => String(item.symbol || "").toUpperCase() !== symbol)
      );
      setPendingOrders((prev) =>
        prev.filter((item) => String(item.symbol || "").toUpperCase() !== symbol)
      );
    }

    if (payload.action === "close_all") {
      setOpenPositions([]);
      setPendingOrders([]);
    }
  }, []);

  const sendExec = useCallback(
    async (payload) => {
      const token = String(agentToken || "").trim();

      if (!token) {
        setErr(t("execution.errors.noToken"));
        return;
      }

      try {
        setSendingExec(true);
        setErr("");

        await apiAgentRegisterExisting(token);
        const liveStatus = await apiAgentStatus(token);
        const connectedNow = isAgentReallyConnected(liveStatus);

        setStatus(liveStatus || null);

        if (!connectedNow) {
          setErr(t("execution.errors.notConnected"));
          return;
        }

        const normalizedPayload = normalizeExecPayload(payload);

        await apiAgentCommand(token, {
          id: Date.now().toString(),
          ...normalizedPayload,
        });

        removeClosedFromUi(payload);
        scheduleRefreshSequence();

        setFlash(t("execution.flash.sent"));
        window.setTimeout(() => setFlash(""), 2200);
      } catch (e) {
        setErr(t("execution.errors.sendFailed", { error: e?.message || e }));
      } finally {
        setSendingExec(false);
      }
    },
    [agentToken, normalizeExecPayload, removeClosedFromUi, scheduleRefreshSequence]
  );

  const hasToken = Boolean((agentToken || "").trim());
  const liveConnected = isAgentReallyConnected(status);
  const balance = formatNumber(pick(status?.account || {}, ["balance"], 0));
  const equity = formatNumber(pick(status?.account || {}, ["equity"], 0));
  const serverLabel = String(
    pick(status?.account || {}, ["server", "trade_server", "broker_server"], "—") || "—"
  ).trim() || "—";
  /* Compte de destination des ordres. Sans cette information, un token présent
     sur deux terminaux rendait impossible de savoir où partait un Buy. */
  const targetAccountLabel = (() => {
    const login = String(
      pick(status?.account || {}, ["login", "account", "number", "id"], "") || "",
    ).trim();
    const server = serverLabel === "—" ? "" : serverLabel;
    const parts = [server, login && `#${login}`].filter(Boolean);
    return parts.length ? parts.join(" • ") : "";
  })();

  const agentOnline = liveConnected;
  const pnlTotal = historyTrades.reduce((sum, trade) => sum + toNumber(trade.profit, 0), 0);
  const recentHistory = historyTrades.slice(0, 8);

  const rrData = useMemo(() => {
    const entry = nfloat(toolRR.entry);
    const stop = nfloat(toolRR.stop);
    const target = nfloat(toolRR.target);
    const side = toolRR.side;

    if (entry == null || stop == null || target == null) {
      return { risk: 0, reward: 0, ratio: 0, valid: false, logicOk: false };
    }

    const risk = Math.abs(entry - stop);
    const reward = Math.abs(target - entry);
    const ratio = risk > 0 ? reward / risk : 0;

    const logicOk =
      side === "buy" ? stop < entry && target > entry : stop > entry && target < entry;

    return { risk, reward, ratio, valid: true, logicOk };
  }, [toolRR]);

  const projectionData = useMemo(() => {
    const entry = nfloat(toolBuilder.entry);
    const slPoints = Number(toolBuilder.slPoints || 0);
    const tpPoints = Number(toolBuilder.tpPoints || 0);
    const pointSize = nfloat(toolBuilder.pointSize);

    if (entry == null || pointSize == null || pointSize <= 0) {
      return { valid: false, slPrice: null, tpPrice: null };
    }

    const slMove = slPoints * pointSize;
    const tpMove = tpPoints * pointSize;

    const slPrice = toolBuilder.side === "buy" ? entry - slMove : entry + slMove;
    const tpPrice = toolBuilder.side === "buy" ? entry + tpMove : entry - tpMove;

    return { valid: true, slPrice, tpPrice };
  }, [toolBuilder]);

  const buildMarketPayload = useCallback(
    (side) => {
      const payload = {
        action: "market_order",
        side,
        symbol: (formMarket.symbol || defaultSymbol).toUpperCase(),
        volume: nfloat(formMarket.volume) ?? 0.1,
      };

      if (formMarket.sl_tp_mode === "price") {
        const slPrice = nfloat(formMarket.sl_price);
        const tpPrice = nfloat(formMarket.tp_price);

        if (slPrice !== undefined) {
          payload.sl = slPrice;
          payload.sl_price = slPrice;
        }

        if (tpPrice !== undefined) {
          payload.tp = tpPrice;
          payload.tp_price = tpPrice;
        }

        return payload;
      }

      payload.sl_points = nfloat(formMarket.sl_points);
      payload.tp_points = nfloat(formMarket.tp_points);
      return payload;
    },
    [defaultSymbol, formMarket]
  );

  const applyMarketPreset = useCallback((sl, tp) => {
    setFormMarket((f) => ({
      ...f,
      sl_tp_mode: "points",
      sl_points: String(sl),
      tp_points: String(tp),
    }));
  }, []);

  return (
    <div className="fm-execution-page relative h-[100dvh] overflow-hidden bg-slate-100 text-slate-900 dark:bg-[#0a0f1c] dark:text-white">
      <style>{`
        .fm-execution-page .fm-exec-header,
        .fm-execution-page .fm-exec-panel,
        .fm-execution-page .fm-exec-card,
        .fm-execution-page .fm-exec-stat{
          background: #ffffff !important;
          border-color: #e2e8f0 !important;
          box-shadow: none !important;
        }
        .fm-execution-page .fm-exec-tab-rail{
          background: #f1f5f9 !important;
          border-color: #e2e8f0 !important;
          box-shadow: none !important;
        }
        .fm-execution-page .fm-market-mode-btn{
          border-color: #e2e8f0;
          background: #ffffff;
          color: #334155;
          box-shadow: none;
        }
        .fm-execution-page .fm-market-mode-btn.is-active{
          border-color: #7c3aed;
          background: rgba(124,58,237,0.10);
          color: #6d28d9;
          box-shadow: none;
        }

        .dark .fm-execution-page .fm-exec-header,
        .dark .fm-execution-page .fm-exec-panel,
        .dark .fm-execution-page .fm-exec-card,
        .dark .fm-execution-page .fm-exec-stat{
          background: #0f1729 !important;
          border-color: #26314a !important;
          box-shadow: none !important;
        }
        .dark .fm-execution-page .fm-exec-tab-rail{
          background: #0b1220 !important;
          border-color: #26314a !important;
          box-shadow: none !important;
        }
        .dark .fm-execution-page .fm-market-mode-btn{
          border-color: #2a3550;
          background: #0f1729;
          color: #cbd5e1;
          box-shadow: none;
        }
        .dark .fm-execution-page .fm-market-mode-btn.is-active{
          border-color: #7c3aed;
          background: rgba(124,58,237,0.22);
          color: #f8fafc;
          box-shadow: none;
        }
        .dark .fm-execution-page .fm-exec-input,
        .dark .fm-execution-page input,
        .dark .fm-execution-page select{
          background-color: #0f1729 !important;
          border-color: #2a3550 !important;
          color: #f8fafc !important;
        }
        .dark .fm-execution-page input::placeholder{
          color: rgba(203, 213, 225, 0.6) !important;
        }
        .dark .fm-execution-page .bg-white\/88,
        .dark .fm-execution-page .bg-white\/86,
        .dark .fm-execution-page .bg-white\/94,
        .dark .fm-execution-page .bg-white\/90,
        .dark .fm-execution-page .bg-white\/80,
        .dark .fm-execution-page .bg-slate-50\/82{
          background-color: #131c30 !important;
        }
        .dark .fm-execution-page .bg-slate-50,
        .dark .fm-execution-page .bg-slate-50\/45{
          background-color: #0f1729 !important;
        }
        .dark .fm-execution-page .border-white\/10,
        .dark .fm-execution-page .border-white\/\[0\.08\]{
          border-color: #26314a !important;
        }
      `}</style>

      <div className="relative flex h-full min-h-0 items-stretch">
      <ExecTabSidebar execTab={execTab} setExecTab={setExecTab} open={tabOpen} setOpen={setTabOpen} />
      <div className="relative mx-auto h-full min-h-0 w-full min-w-0 flex-1 max-w-[1720px] overflow-y-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-8 2xl:px-12">
        {/* Desktop : en-tête toujours visible au-dessus du formulaire.
            Mobile : c'est la section « Aperçu » du sous-menu. */}
        {(isDesktop || execTab === OVERVIEW_KEY) && (
        <div className={`${headerShellClass} mb-4 overflow-hidden p-4 sm:p-5 lg:p-6`}>
          <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,520px)] xl:items-stretch xl:gap-5">
            {/* Description et puces hors de la colonne de l'icône : sinon elles
                démarrent ~60px plus à droite et perdent d'autant de largeur. */}
            <div className="min-w-0 rounded-[24px] border border-transparent p-0 xl:flex xl:items-center">
              {/* En-tête commun à toutes les pages (PageHeader). */}
              <PageHeader
                icon={SendHorizontal}
                title={t("execution.header.title")}
                subtitle={t("execution.header.subtitle")}
                chips={
                  <>
                    <Chip tone={hasToken ? "emerald" : "amber"}>
                      {hasToken ? t("execution.header.tokenDetected") : t("execution.header.tokenMissing")}
                    </Chip>
                    <Chip tone={liveConnected ? "emerald" : "amber"}>
                      {liveConnected ? t("execution.header.agentOnline") : t("execution.header.agentOffline")}
                    </Chip>
                    <Chip tone="sky">{t("execution.header.balanceChip", { value: balance })}</Chip>
                    {/* Destination des ordres, explicite : le token peut être
                        présent sur plusieurs terminaux MT5. */}
                    {targetAccountLabel ? (
                      <Chip tone="violet">Ordres vers : {targetAccountLabel}</Chip>
                    ) : null}
                  </>
                }
              />
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:w-full">
              <div className={compactStatCardClass}>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                      {t("execution.stats.balance")}
                    </div>
                    <div className="mt-2 truncate text-lg font-semibold text-slate-950 dark:text-white">
                      {balance}
                    </div>
                  </div>
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-violet-500/10 text-violet-700 ring-1 ring-violet-500/20 dark:text-violet-300">
                    <Wallet size={17} />
                  </span>
                </div>
              </div>

              <div className={compactStatCardClass}>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                      {t("execution.stats.equity")}
                    </div>
                    <div className="mt-2 truncate text-lg font-semibold text-slate-950 dark:text-white">
                      {equity}
                    </div>
                  </div>
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/20 dark:text-emerald-300">
                    <Activity size={17} />
                  </span>
                </div>
              </div>

              <div className={`${compactStatCardClass} sm:col-span-2`}>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                      {t("execution.stats.server")}
                    </div>
                    <div
                      title={serverLabel}
                      className="mt-2 max-w-full break-words text-sm font-semibold leading-snug text-slate-950 [overflow-wrap:anywhere] dark:text-white sm:text-[15px]"
                    >
                      {serverLabel}
                    </div>
                  </div>
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-sky-500/10 text-sky-700 ring-1 ring-sky-500/20 dark:text-sky-300">
                    <Globe size={17} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Confirmations et erreurs : rendues en flottant plus bas (FlashToast),
            plus rien ici. Un bandeau à cet endroit poussait le formulaire vers
            le bas à chaque envoi d'ordre, puis le remontait 2 s après. */}

        <ExecutionRequirementCard hasToken={hasToken} isConnected={liveConnected} />

        {/* Sur « Aperçu » aucun formulaire n'est rendu : sans ce garde, la section
            resterait affichée avec son fond et son padding — une carte vide. */}
        {execTab !== OVERVIEW_KEY && (
        <section className={`${panelClass} overflow-hidden`}>
          <div className="p-3.5 sm:p-5 lg:p-6">
            {execTab === "market" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader
                  title={t("execution.market.title")}
                  subtitle={t("execution.market.subtitle")}
                  right={<Chip tone="neutral">{(formMarket.symbol || defaultSymbol).toUpperCase()}</Chip>}
                />

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label={t("execution.common.symbol")}>
                    <SymbolInput
                      value={formMarket.symbol}
                      onChange={(symbol) => setFormMarket((f) => ({ ...f, symbol }))}
                      options={symbolOptions}
                      placeholder={t("execution.common.symbolPlaceholder")}
                    />
                  </Field>

                  <Field label={t("execution.market.slTpMode")} hint={t("execution.market.slTpModeHint")}>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormMarket((f) => ({ ...f, sl_tp_mode: "points" }))}
                        className={`fm-market-mode-btn ${formMarket.sl_tp_mode !== "price" ? "is-active" : ""} h-12 rounded-2xl border px-3 text-sm font-semibold transition`}
                      >
                        {t("execution.market.points")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormMarket((f) => ({ ...f, sl_tp_mode: "price" }))}
                        className={`fm-market-mode-btn ${formMarket.sl_tp_mode === "price" ? "is-active" : ""} h-12 rounded-2xl border px-3 text-sm font-semibold transition`}
                      >
                        {t("execution.market.directPrice")}
                      </button>
                    </div>
                  </Field>

                  {formMarket.sl_tp_mode === "price" ? (
                    <>
                      <Field label={t("execution.market.slPriceLabel")}>
                        <input
                          value={formMarket.sl_price}
                          onChange={(e) => setFormMarket((f) => ({ ...f, sl_price: e.target.value }))}
                          placeholder={t("execution.market.slPricePlaceholder")}
                          className={inputClass}
                        />
                      </Field>

                      <Field label={t("execution.market.tpPriceLabel")}>
                        <input
                          value={formMarket.tp_price}
                          onChange={(e) => setFormMarket((f) => ({ ...f, tp_price: e.target.value }))}
                          placeholder={t("execution.market.tpPricePlaceholder")}
                          className={inputClass}
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label={t("execution.market.slPoints")}>
                        <input
                          value={formMarket.sl_points}
                          onChange={(e) => setFormMarket((f) => ({ ...f, sl_points: e.target.value }))}
                          placeholder="100"
                          className={inputClass}
                        />
                      </Field>

                      <Field label={t("execution.market.tpPoints")}>
                        <input
                          value={formMarket.tp_points}
                          onChange={(e) => setFormMarket((f) => ({ ...f, tp_points: e.target.value }))}
                          placeholder="200"
                          className={inputClass}
                        />
                      </Field>

                      <Field label={t("execution.market.shortcuts")}>
                        <div className="flex flex-wrap gap-2">
                          <QuickPreset label="30 / 60" onClick={() => applyMarketPreset(30, 60)} />
                          <QuickPreset label="80 / 160" onClick={() => applyMarketPreset(80, 160)} />
                          <QuickPreset label="150 / 300" onClick={() => applyMarketPreset(150, 300)} />
                        </div>
                      </Field>
                    </>
                  )}
                </div>

                <div className="mt-5 rounded-[26px] border border-slate-200/70 bg-slate-50/82 p-3.5 dark:border-white/[0.08] dark:bg-[#0d1222]/82 sm:p-5">
                  <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    {t("execution.market.directExec")}
                  </div>

                  <div className="grid grid-cols-[minmax(0,1fr)_124px_minmax(0,1fr)] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)] sm:gap-3">
                    <button
                      type="button"
                      disabled={sendingExec}
                      onClick={() =>
                        sendExec(buildMarketPayload("sell"))
                      }
                      className="inline-flex min-h-[60px] items-center justify-center gap-2 rounded-[22px] bg-rose-600 px-2 text-sm font-semibold text-white  transition-all duration-200  hover:bg-rose-700 disabled:opacity-60 sm:min-h-[66px]"
                    >
                      {sendingExec ? <RefreshCw size={16} className="animate-spin" /> : <ChevronRight size={16} className="rotate-180" />}
                      <span>{t("execution.common.sell")}</span>
                    </button>

                    <LotCenterControl
                      value={String(formMarket.volume)}
                      onChange={(volume) => setFormMarket((f) => ({ ...f, volume }))}
                    />

                    <button
                      type="button"
                      disabled={sendingExec}
                      onClick={() =>
                        sendExec(buildMarketPayload("buy"))
                      }
                      className="inline-flex min-h-[60px] items-center justify-center gap-2 rounded-[22px] bg-emerald-600 px-2 text-sm font-semibold text-white  transition-all duration-200  hover:bg-emerald-700 disabled:opacity-60 sm:min-h-[66px]"
                    >
                      {sendingExec ? <RefreshCw size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                      <span>{t("execution.common.buy")}</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {execTab === "pending" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.pending.title")} subtitle={t("execution.pending.subtitle")} />

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <Field label={t("execution.pending.type")}>
                    <select
                      value={formPending.type}
                      onChange={(e) => setFormPending((f) => ({ ...f, type: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="buy_limit">Buy Limit</option>
                      <option value="sell_limit">Sell Limit</option>
                      <option value="buy_stop">Buy Stop</option>
                      <option value="sell_stop">Sell Stop</option>
                    </select>
                  </Field>

                  <Field label={t("execution.common.symbol")}>
                    <SymbolInput
                      value={formPending.symbol}
                      onChange={(symbol) => setFormPending((f) => ({ ...f, symbol }))}
                      options={symbolOptions}
                      placeholder={t("execution.common.symbolPlaceholder")}
                    />
                  </Field>

                  <Field label={t("execution.common.volume")}>
                    <input
                      type="number"
                      step="0.01"
                      value={formPending.volume}
                      onChange={(e) => setFormPending((f) => ({ ...f, volume: e.target.value }))}
                      placeholder="0.10"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.pending.price")}>
                    <input
                      value={formPending.price}
                      onChange={(e) => setFormPending((f) => ({ ...f, price: e.target.value }))}
                      placeholder="1.08450"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.pending.slPoints")}>
                    <input
                      value={formPending.sl_points}
                      onChange={(e) => setFormPending((f) => ({ ...f, sl_points: e.target.value }))}
                      placeholder="100"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.pending.tpPoints")}>
                    <input
                      value={formPending.tp_points}
                      onChange={(e) => setFormPending((f) => ({ ...f, tp_points: e.target.value }))}
                      placeholder="200"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <button
                  type="button"
                  disabled={sendingExec}
                  onClick={() =>
                    sendExec({
                      action: "pending_order",
                      type: formPending.type,
                      symbol: formPending.symbol,
                      volume: nfloat(formPending.volume) ?? 0.1,
                      price: nfloat(formPending.price),
                      sl_points: nfloat(formPending.sl_points),
                      tp_points: nfloat(formPending.tp_points),
                    })
                  }
                  className="mt-5 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 text-sm font-semibold text-white transition-all duration-200  hover:bg-violet-700 disabled:opacity-60"
                >
                  {sendingExec ? <RefreshCw size={16} className="animate-spin" /> : <Clock size={16} />}
                  {t("execution.pending.place")}
                </button>
              </div>
            ) : null}

            {execTab === "modify" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.modify.title")} subtitle={t("execution.modify.subtitle")} />

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <Field label={t("execution.common.scope")}>
                    <select
                      value={formModify.scope}
                      onChange={(e) => {
                        const scope = e.target.value;
                        setFormModify((f) => ({
                          ...f,
                          scope,
                          unit: scope === "all" ? "price" : f.unit,
                          sl_points: scope === "all" ? "" : f.sl_points,
                          tp_points: scope === "all" ? "" : f.tp_points,
                        }));
                      }}
                      className={selectClass}
                    >
                      <option value="symbol">{t("execution.modify.scopeSymbol")}</option>
                      <option value="all">{t("execution.modify.scopeAll")}</option>
                    </select>
                  </Field>

                  {formModify.scope === "symbol" ? (
                    <Field label={t("execution.common.symbol")}>
                      <SymbolInput
                        value={formModify.symbol}
                        onChange={(symbol) => setFormModify((f) => ({ ...f, symbol }))}
                        options={symbolOptions}
                        placeholder={t("execution.common.symbolPlaceholder")}
                      />
                    </Field>
                  ) : (
                    <Field label={t("execution.modify.target")}>
                      <div className="flex h-12 items-center rounded-2xl border border-slate-200/80 bg-slate-50 px-4 text-sm font-medium text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
                        {t("execution.modify.allActive")}
                      </div>
                    </Field>
                  )}

                  <Field label={t("execution.modify.inputMode")}>
                    <select
                      value={formModify.scope === "all" ? "price" : formModify.unit}
                      onChange={(e) => setFormModify((f) => ({ ...f, unit: e.target.value }))}
                      className={selectClass}
                      disabled={formModify.scope === "all"}
                    >
                      <option value="points">{t("execution.modify.pointsOption")}</option>
                      <option value="price">{t("execution.modify.priceOption")}</option>
                    </select>
                  </Field>

                  <Field label={formModify.scope === "all" || formModify.unit === "price" ? t("execution.modify.slPrice") : t("execution.modify.slPoints")}>
                    <input
                      value={formModify.scope === "all" || formModify.unit === "price" ? formModify.sl_price : formModify.sl_points}
                      onChange={(e) =>
                        setFormModify((f) => ({
                          ...f,
                          [formModify.scope === "all" || formModify.unit === "price" ? "sl_price" : "sl_points"]: e.target.value,
                        }))
                      }
                      placeholder={formModify.scope === "all" || formModify.unit === "price" ? "1.08450" : "100"}
                      className={inputClass}
                    />
                  </Field>

                  <Field label={formModify.scope === "all" || formModify.unit === "price" ? t("execution.modify.tpPrice") : t("execution.modify.tpPoints")}>
                    <input
                      value={formModify.scope === "all" || formModify.unit === "price" ? formModify.tp_price : formModify.tp_points}
                      onChange={(e) =>
                        setFormModify((f) => ({
                          ...f,
                          [formModify.scope === "all" || formModify.unit === "price" ? "tp_price" : "tp_points"]: e.target.value,
                        }))
                      }
                      placeholder={formModify.scope === "all" || formModify.unit === "price" ? "1.08650" : "200"}
                      className={inputClass}
                    />
                  </Field>
                </div>

                {formModify.scope === "all" ? (
                  <div className="mt-4 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
                    {t("execution.modify.allWarning")}
                  </div>
                ) : null}

                <button
                  type="button"
                  disabled={sendingExec}
                  onClick={() =>
                    sendExec({
                      action: "modify_sl_tp",
                      scope: formModify.scope,
                      unit: formModify.scope === "all" ? "price" : formModify.unit,
                      symbol: formModify.symbol,
                      sl_points: nfloat(formModify.sl_points),
                      tp_points: nfloat(formModify.tp_points),
                      sl_price: nfloat(formModify.sl_price),
                      tp_price: nfloat(formModify.tp_price),
                    })
                  }
                  className="mt-5 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 text-sm font-semibold text-white transition-all duration-200  hover:bg-violet-700 disabled:opacity-60"
                >
                  {sendingExec ? <RefreshCw size={16} className="animate-spin" /> : <Pencil size={16} />}
                  {t("execution.modify.submit")}
                </button>
              </div>
            ) : null}

            {execTab === "close" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.close.title")} subtitle={t("execution.close.subtitle")} />

                <div className="grid gap-4 md:grid-cols-1 xl:max-w-[460px]">
                  <Field label={t("execution.common.symbol")}>
                    <SymbolInput
                      value={formClose.symbol}
                      onChange={(symbol) => setFormClose((f) => ({ ...f, symbol }))}
                      options={symbolOptions}
                      placeholder={t("execution.common.symbolPlaceholder")}
                    />
                  </Field>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  <button
                    type="button"
                    disabled={sendingExec}
                    onClick={() => sendExec({ action: "close_symbol", symbol: formClose.symbol })}
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-rose-600/90 px-4 text-sm font-semibold text-white transition-all duration-200  hover:bg-rose-700 disabled:opacity-60"
                  >
                    <XCircle size={16} />
                    {t("execution.close.closeSymbol")}
                  </button>

                  <button
                    type="button"
                    disabled={sendingExec}
                    onClick={() => sendExec({ action: "close_all" })}
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 text-sm font-semibold text-rose-700 transition-all duration-200 hover:bg-rose-500/15 disabled:opacity-60 dark:text-rose-300"
                  >
                    <AlertTriangle size={16} />
                    {t("execution.close.closeAll")}
                  </button>
                </div>
              </div>
            ) : null}

            {execTab === "be" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.be.title")} subtitle={t("execution.be.subtitle")} />

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t("execution.common.scope")}>
                    <select
                      value={formBE.scope}
                      onChange={(e) => setFormBE((f) => ({ ...f, scope: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="symbol">{t("execution.be.scopeSymbol")}</option>
                      <option value="all">{t("execution.be.scopeAll")}</option>
                    </select>
                  </Field>

                  <Field label={t("execution.common.symbol")}>
                    <SymbolInput
                      value={formBE.symbol}
                      onChange={(symbol) => setFormBE((f) => ({ ...f, symbol }))}
                      options={symbolOptions}
                      placeholder={t("execution.common.symbolPlaceholder")}
                    />
                  </Field>

                  <Field label={t("execution.be.offset")}>
                    <input
                      type="number"
                      value={formBE.offset_points}
                      onChange={(e) => setFormBE((f) => ({ ...f, offset_points: e.target.value }))}
                      placeholder="0"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.be.condition")}>
                    <label className="flex h-12 items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/95 px-4 text-sm text-slate-700 dark:border-white/10 dark:bg-[#0f1529] dark:text-white">
                      <input
                        type="checkbox"
                        checked={formBE.only_in_profit}
                        onChange={(e) => setFormBE((f) => ({ ...f, only_in_profit: e.target.checked }))}
                      />
                      {t("execution.be.onlyProfit")}
                    </label>
                  </Field>
                </div>

                <button
                  type="button"
                  disabled={sendingExec}
                  onClick={() =>
                    sendExec({
                      action: "set_break_even",
                      scope: formBE.scope,
                      symbol: formBE.symbol,
                      offset_points: Number(formBE.offset_points || 0),
                      only_in_profit: !!formBE.only_in_profit,
                    })
                  }
                  className="mt-5 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 text-sm font-semibold text-white transition-all duration-200  hover:bg-violet-700 disabled:opacity-60"
                >
                  <MoveRight size={16} />
                  {t("execution.be.submit")}
                </button>
              </div>
            ) : null}

            {execTab === "trailing" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.trailing.title")} subtitle={t("execution.trailing.subtitle")} />

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t("execution.common.scope")}>
                    <select
                      value={formTrail.scope}
                      onChange={(e) => setFormTrail((f) => ({ ...f, scope: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="symbol">{t("execution.trailing.scopeSymbol")}</option>
                      <option value="all">{t("execution.trailing.scopeAll")}</option>
                    </select>
                  </Field>

                  <Field label={t("execution.common.symbol")}>
                    <SymbolInput
                      value={formTrail.symbol}
                      onChange={(symbol) => setFormTrail((f) => ({ ...f, symbol }))}
                      options={symbolOptions}
                      placeholder={t("execution.common.symbolPlaceholder")}
                    />
                  </Field>

                  <Field label={t("execution.trailing.points")}>
                    <input
                      type="number"
                      value={formTrail.points}
                      onChange={(e) => setFormTrail((f) => ({ ...f, points: e.target.value }))}
                      placeholder="100"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    disabled={sendingExec}
                    onClick={() =>
                      sendExec({
                        action: "enable_trailing",
                        scope: formTrail.scope,
                        symbol: formTrail.symbol,
                        points: Number(formTrail.points || 0),
                      })
                    }
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 text-sm font-semibold text-white transition-all duration-200  hover:bg-violet-700 disabled:opacity-60"
                  >
                    <GitBranchPlus size={16} />
                    {t("execution.trailing.enable")}
                  </button>

                  <button
                    type="button"
                    disabled={sendingExec}
                    onClick={() =>
                      sendExec({
                        action: "disable_trailing",
                        scope: formTrail.scope,
                        symbol: formTrail.symbol,
                      })
                    }
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-slate-200/80 bg-white/90 px-5 text-sm font-semibold text-slate-800 transition-all duration-200  hover:bg-white disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:bg-white/[0.09]"
                  >
                    <XCircle size={16} />
                    {t("execution.trailing.disable")}
                  </button>
                </div>
              </div>
            ) : null}

            {execTab === "rr" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.rr.title")} subtitle={t("execution.rr.subtitle")} />

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t("execution.common.side")}>
                    <select
                      value={toolRR.side}
                      onChange={(e) => setToolRR((s) => ({ ...s, side: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="buy">{t("execution.common.buy")}</option>
                      <option value="sell">{t("execution.common.sell")}</option>
                    </select>
                  </Field>

                  <Field label={t("execution.common.entry")}>
                    <input
                      value={toolRR.entry}
                      onChange={(e) => setToolRR((s) => ({ ...s, entry: e.target.value }))}
                      placeholder="1.08450"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.common.stop")}>
                    <input
                      value={toolRR.stop}
                      onChange={(e) => setToolRR((s) => ({ ...s, stop: e.target.value }))}
                      placeholder="1.08300"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.common.target")}>
                    <input
                      value={toolRR.target}
                      onChange={(e) => setToolRR((s) => ({ ...s, target: e.target.value }))}
                      placeholder="1.08750"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-white/[0.04]">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.rr.risk")}</div>
                    <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                      {rrData.valid ? rrData.risk.toFixed(5) : "—"}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-white/[0.04]">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.rr.reward")}</div>
                    <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                      {rrData.valid ? rrData.reward.toFixed(5) : "—"}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-white/[0.04]">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.rr.ratio")}</div>
                    <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                      {rrData.valid ? rrData.ratio.toFixed(2) : "—"}
                    </div>
                  </div>
                </div>

                {rrData.valid ? (
                  <div
                    className={`mt-4 rounded-2xl px-4 py-3 text-sm font-medium ${
                      rrData.logicOk
                        ? "border border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : "border border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                    }`}
                  >
                    {rrData.logicOk
                      ? t("execution.rr.coherent")
                      : t("execution.rr.incoherent")}
                  </div>
                ) : null}
              </div>
            ) : null}

            {execTab === "builder" ? (
              <div className={`${cardClass} p-4 sm:p-5`}>
                <SectionHeader title={t("execution.builder.title")} subtitle={t("execution.builder.subtitle")} />

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t("execution.common.side")}>
                    <select
                      value={toolBuilder.side}
                      onChange={(e) => setToolBuilder((s) => ({ ...s, side: e.target.value }))}
                      className={selectClass}
                    >
                      <option value="buy">{t("execution.common.buy")}</option>
                      <option value="sell">{t("execution.common.sell")}</option>
                    </select>
                  </Field>

                  <Field label={t("execution.common.entry")}>
                    <input
                      value={toolBuilder.entry}
                      onChange={(e) => setToolBuilder((s) => ({ ...s, entry: e.target.value }))}
                      placeholder="1.08450"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.builder.slPoints")}>
                    <input
                      value={toolBuilder.slPoints}
                      onChange={(e) => setToolBuilder((s) => ({ ...s, slPoints: e.target.value }))}
                      placeholder="100"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.builder.tpPoints")}>
                    <input
                      value={toolBuilder.tpPoints}
                      onChange={(e) => setToolBuilder((s) => ({ ...s, tpPoints: e.target.value }))}
                      placeholder="200"
                      className={inputClass}
                    />
                  </Field>

                  <Field label={t("execution.builder.pointSize")}>
                    <input
                      value={toolBuilder.pointSize}
                      onChange={(e) => setToolBuilder((s) => ({ ...s, pointSize: e.target.value }))}
                      placeholder="0.0001"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-white/[0.04]">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.builder.slPrice")}</div>
                    <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                      {projectionData.valid ? Number(projectionData.slPrice).toFixed(5) : "—"}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-white/[0.04]">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">{t("execution.builder.tpPrice")}</div>
                    <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                      {projectionData.valid ? Number(projectionData.tpPrice).toFixed(5) : "—"}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </section>
        )}

        <section className={`${panelClass} mt-4 p-4 sm:p-5`}>
          <SectionHeader
            title={t("execution.sections.openPositions")}
            right={<Chip tone="neutral">{openPositions.length}</Chip>}
          />

          <OpenPositionsTable items={openPositions} />
        </section>

        {pendingOrders.length > 0 ? (
          <section className={`${panelClass} mt-4 p-4 sm:p-5`}>
            <SectionHeader
              title={t("execution.sections.pendingOrders")}
              subtitle={t("execution.sections.pendingSubtitle")}
              right={<Chip tone="neutral">{pendingOrders.length}</Chip>}
            />

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {pendingOrders.map((order) => (
                <HorizontalTradeCard key={`${order.ticket}-${order.symbol}`} item={order} pending />
              ))}
            </div>
          </section>
        ) : null}

        {recentHistory.length > 0 ? (
          <section className={`${panelClass} mt-4 p-4 sm:p-5`}>
            <SectionHeader
              title={t("execution.sections.historyTitle")}
              subtitle={t("execution.sections.historySubtitle")}
              right={<Chip tone="neutral">{historyTrades.length}</Chip>}
            />

            <div className="grid gap-3 lg:hidden">
              {recentHistory.map((trade) => (
                <HistoryCard
                  key={`${trade.ticket}-${trade.symbol}-${trade.close_time || trade.open_time || ""}`}
                  item={trade}
                />
              ))}
            </div>

            <div className="hidden overflow-hidden rounded-[22px] border border-slate-200/70 dark:border-white/10 lg:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200/70 dark:divide-white/10">
                  <thead className="bg-slate-50/80 dark:bg-white/[0.03]">
                    <tr>
                      {[
                        t("execution.historyTable.ticket"),
                        t("execution.historyTable.symbol"),
                        t("execution.historyTable.side"),
                        t("execution.historyTable.volume"),
                        t("execution.historyTable.open"),
                        t("execution.historyTable.close"),
                        t("execution.historyTable.profit"),
                      ].map((head) => (
                        <th
                          key={head}
                          className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300"
                        >
                          {head}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/70 dark:divide-white/10">
                    {recentHistory.map((trade) => {
                      const positive = toNumber(trade.profit, 0) >= 0;
                      return (
                        <tr
                          key={`${trade.ticket}-${trade.symbol}-${trade.close_time || trade.open_time || ""}`}
                          className="bg-white/70 dark:bg-transparent"
                        >
                          <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">
                            #{trade.ticket}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-white">
                            {trade.symbol}
                          </td>
                          <td className="px-4 py-3 text-sm uppercase text-slate-700 dark:text-slate-200">
                            {trade.side || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                            {trade.volume}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                            {trade.open_price || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                            {trade.close_price || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={[
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                                positive
                                  ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300"
                                  : "bg-rose-500/10 text-rose-700 ring-rose-500/20 dark:text-rose-300",
                              ].join(" ")}
                            >
                              {positive ? "+" : ""}
                              {formatNumber(trade.profit)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ) : null}
      </div>
      </div>

      {/* Confirmations et erreurs, hors du flux : la page ne se décale plus
          quand elles apparaissent ou disparaissent. L'erreur ne se referme
          pas toute seule — elle doit pouvoir être lue et relue. */}
      <FlashToast tone="success" text={flash} onClose={() => setFlash("")} />
      <FlashToast tone="error" text={err} onClose={() => setErr("")} />
    </div>
  );
}
